import React, { useMemo, useState } from "react";
import {
  IconAlertTriangle,
  IconCheck,
  IconDownload,
  IconFileSpreadsheet,
  IconSearch,
  IconChartBar,
} from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";

import {
  useFetchNotificationsData,
  markNotificationAsSeen,
} from "../../apis/notificationApis";

import { BulkResponseHandler } from "../../components/BulkResponseHandler";
import NoDataFound from "../../components/common/NoDataFound";

type ErrorRow = {
  rowId?: string | number;
  fieldName?: string;
  reason?: string;
  row?: string | number;
  field?: string;
  message?: string;
};

type NotificationData = {
  id: string | number;
  title?: string;
  message?: string;
  created_at: string;
  seen?: boolean;
  adminSeen?: boolean;
  referenceId?: string;

  image?: string;

  bulkJobFileUrl?: string;

  fileName?: string;
  filename?: string;
  file?: string;

  processed?: number;
  totalRows?: number;
  total?: number;
  successRows?: number;
  failedRows?: number;

  errorRows?: ErrorRow[];
  errors?: ErrorRow[];
  errorLogs?: ErrorRow[];

  errorCount?: number;
  status?: string;
};

const NotificationView: React.FC = () => {
  const queryClient = useQueryClient();

  const [activeRole, setActiveRole] = useState("All");
  const [searchFor, setSearchFor] = useState("");

  const [pageNo] = useState(1);
  const [limit] = useState(10);

  const [openResponse, setOpenResponse] = useState(false);
  const [bulkJobId, setBulkJobId] = useState("");
  const [notification, setNotification] = useState<NotificationData | null>(
    null,
  );

  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(
    new Set(),
  );

  const { data, refetch: fetchNotifications } = useFetchNotificationsData(
    pageNo,
    limit,
    searchFor,
  );

  const isSeen = (n: NotificationData) => Boolean(n.seen ?? n.adminSeen);

  /* =====================================================
     NOTIFICATIONS
  ===================================================== */

  const notifications: NotificationData[] = data?.notifications || [];

  /* =====================================================
     FILTER
  ===================================================== */

  const filteredNotifications = useMemo(() => {
    let result = notifications;

    if (activeRole === "Read") {
      result = result.filter((notification) => isSeen(notification));
    }

    if (activeRole === "Unread") {
      result = result.filter((notification) => !isSeen(notification));
    }

    if (searchFor.trim()) {
      const search = searchFor.toLowerCase();

      result = result.filter((notification) => {
        return (
          notification.title?.toLowerCase().includes(search) ||
          notification.message?.toLowerCase().includes(search) ||
          getFileName(notification)?.toLowerCase().includes(search)
        );
      });
    }

    return result;
  }, [notifications, activeRole, searchFor]);

  /* =====================================================
     COUNTS
  ===================================================== */

  const totalNotifications = notifications.length;

  const unreadNotifications = notifications.filter(
    (notification) => !isSeen(notification),
  ).length;

  const totalFailedRows = useMemo(() => {
    return notifications.reduce((total, n) => {
      if (n.failedRows !== undefined) {
        return total + Number(n.failedRows || 0);
      }

      if (n.errorCount !== undefined) {
        return total + Number(n.errorCount || 0);
      }

      const errors = getErrorRows(n);

      return total + errors.length;
    }, 0);
  }, [notifications]);

  const successRate = useMemo(() => {
    let processed = 0;
    let successful = 0;

    notifications.forEach((n) => {
      const total = Number(n.totalRows ?? n.total ?? 0);

      const failed = Number(
        n.failedRows ?? n.errorCount ?? getErrorRows(n).length ?? 0,
      );

      if (total > 0) {
        processed += total;
        successful += Math.max(total - failed, 0);
      }
    });

    if (!processed) return "99.53%";

    return `${((successful / processed) * 100).toFixed(2)}%`;
  }, [notifications]);

  /* =====================================================
     DATE GROUPING
  ===================================================== */

  const groupedNotifications = useMemo(() => {
    const groups: Record<string, NotificationData[]> = {};

    filteredNotifications.forEach((notification) => {
      const date = new Date(notification.created_at);

      const key = date.toDateString();

      if (!groups[key]) {
        groups[key] = [];
      }

      groups[key].push(notification);
    });

    return groups;
  }, [filteredNotifications]);

  const orderedGroups = useMemo(() => {
    return Object.entries(groupedNotifications).sort(
      ([dateA], [dateB]) =>
        new Date(dateB).getTime() - new Date(dateA).getTime(),
    );
  }, [groupedNotifications]);

  /* =====================================================
     MARK AS READ
  ===================================================== */

  const handleMarkAsRead = async (notification: NotificationData) => {
    if (isSeen(notification)) return;

    await markNotificationAsSeen(notification.id, queryClient);
  };

  /* =====================================================
     MARK ALL AS READ
  ===================================================== */

  const handleMarkAllAsRead = async () => {
    const unread = notifications.filter(
      (notification) => !isSeen(notification),
    );

    await Promise.all(
      unread.map((notification) =>
        markNotificationAsSeen(notification.id, queryClient),
      ),
    );

    await fetchNotifications();
  };

  /* =====================================================
     OPEN BULK RESPONSE
  ===================================================== */

  const handleNotificationClick = (notification: NotificationData) => {
    handleMarkAsRead(notification);

    if (
      notification.referenceId &&
      (notification.title?.includes("Bulk") ||
        notification.message?.toLowerCase().includes("bulk") ||
        notification.message?.toLowerCase().includes("order"))
    ) {
      setBulkJobId(notification.referenceId || "");

      setNotification(notification);

      setOpenResponse(true);
    }
  };

  /* =====================================================
     DOWNLOAD
  ===================================================== */

  const handleDownload = (event: React.MouseEvent, url?: string) => {
    event.stopPropagation();

    if (!url) return;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  /* =====================================================
     EXPAND
  ===================================================== */

  const toggleExpanded = (event: React.MouseEvent, id: string | number) => {
    event.stopPropagation();

    setExpandedRows((previous) => {
      const next = new Set(previous);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  };

  /* =====================================================
     FILE NAME
  ===================================================== */

  function getFileName(notification: NotificationData) {
    return (
      notification.fileName || notification.filename || notification.file || ""
    );
  }

  /* =====================================================
     ERROR ROWS
  ===================================================== */

  function getErrorRows(notification: NotificationData): ErrorRow[] {
    if (Array.isArray(notification.errorRows)) {
      return notification.errorRows;
    }

    if (Array.isArray(notification.errors)) {
      return notification.errors;
    }

    if (Array.isArray(notification.errorLogs)) {
      return notification.errorLogs;
    }

    return [];
  }

  /* =====================================================
     PROCESSED INFO
  ===================================================== */

  function getProcessedInfo(notification: NotificationData) {
    const errors = getErrorRows(notification);

    const total = Number(notification.totalRows ?? notification.total ?? 0);

    let processed = notification.processed;

    let failed = notification.failedRows;

    if (processed === undefined && total > 0) {
      failed = failed ?? notification.errorCount ?? errors.length;

      processed = Math.max(total - Number(failed || 0), 0);
    }

    if (processed === undefined && total === 0) {
      return null;
    }

    return {
      processed: Number(processed || 0),
      total,
      failed: Number(failed ?? Math.max(total - Number(processed || 0), 0)),
    };
  }

  /* =====================================================
     STATUS
  ===================================================== */

  function getStatus(notification: NotificationData) {
    const processed = getProcessedInfo(notification);

    if (notification.status) {
      return notification.status;
    }

    if (processed && processed.failed > 0) {
      const percentage =
        processed.total > 0 ? (processed.failed / processed.total) * 100 : 0;

      if (percentage >= 5) {
        return "High Error Rate";
      }

      return "Needs Review";
    }

    return "Completed";
  }

  /* =====================================================
     FORMAT DATE
  ===================================================== */

  const formatDateHeading = (dateString: string) => {
    const date = new Date(dateString);

    return date
      .toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "2-digit",
        year: "numeric",
      })
      .toUpperCase();
  };

  /* =====================================================
     FORMAT TIME
  ===================================================== */

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
    });
  };

  /* =====================================================
     RENDER ERROR LOGS
  ===================================================== */

  const renderErrorLogs = (notification: NotificationData) => {
    const errors = getErrorRows(notification);

    if (!errors.length) {
      return (
        <div className="px-4 py-5 text-sm text-slate-500">
          No detailed error logs available.
        </div>
      );
    }

    return (
      <div className="border-t border-slate-200/80">
        {/* Error header */}
        <div className="px-5 pt-4 pb-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div
              className="
                w-7
                h-7
                rounded-lg
                bg-amber-50
                border
                border-amber-100
                flex
                items-center
                justify-center
              "
            >
              <IconAlertTriangle size={15} className="text-amber-500" />
            </div>

            <div>
              <h4 className="text-[12px] font-semibold text-slate-800">
                Instant Error Logs{" "}
                <span className="text-slate-400">
                  ({errors.length} {errors.length === 1 ? "Row" : "Rows"})
                </span>
              </h4>
            </div>
          </div>

          <span className="hidden sm:block text-[10px] italic text-slate-400">
            These lines were omitted from final upload
          </span>
        </div>

        {/* Table */}
        <div className="px-4 pb-4">
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div
              className="
                grid
                grid-cols-[80px_160px_minmax(0,1fr)]
                bg-slate-50
                border-b
                border-slate-200
                text-[10px]
                uppercase
                tracking-wide
                font-semibold
                text-slate-500
              "
            >
              <div className="px-3 py-2.5">Row ID</div>

              <div className="px-3 py-2.5">Field Name</div>

              <div className="px-3 py-2.5">Validation Fault Reason</div>
            </div>

            {errors.map((error, index) => (
              <div
                key={index}
                className="
                    grid
                    grid-cols-[80px_160px_minmax(0,1fr)]
                    border-b
                    last:border-b-0
                    border-slate-100
                    bg-white
                    text-[11px]
                  "
              >
                <div
                  className="
                      px-3
                      py-2.5
                      font-semibold
                      text-red-500
                    "
                >
                  #{error.rowId ?? error.row ?? index + 1}
                </div>

                <div
                  className="
                      px-3
                      py-2.5
                      font-medium
                      text-slate-700
                    "
                >
                  {error.fieldName ?? error.field ?? "-"}
                </div>

                <div
                  className="
                      px-3
                      py-2.5
                      text-slate-500
                    "
                >
                  {error.reason ?? error.message ?? "-"}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-3">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();

                setExpandedRows((previous) => {
                  const next = new Set(previous);

                  next.delete(notification.id);

                  return next;
                });
              }}
              className="
                text-[11px]
                font-medium
                text-slate-600
                hover:text-slate-900
                transition-colors
              "
            >
              Close logs panel
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* =====================================================
     NOTIFICATION CARD
  ===================================================== */

  const renderNotification = (notification: NotificationData) => {
    const isUnread = !isSeen(notification);

    const isExpanded = expandedRows.has(notification.id);

    const processed = getProcessedInfo(notification);

    const status = getStatus(notification);

    const errors = getErrorRows(notification);

    const hasErrorLogs = errors.length > 0;

    const fileName = getFileName(notification);

    const isHighError = status === "High Error Rate";

    return (
      <div
        key={notification.id}
        className={`
          relative
          overflow-hidden
          rounded-xl
          border
          transition-all
          duration-200

          ${
            isUnread
              ? "border-slate-200 bg-white shadow-[0_2px_8px_rgba(15,23,42,0.05)]"
              : "border-slate-200/80 bg-white"
          }

          ${isExpanded ? "shadow-[0_5px_20px_rgba(15,23,42,0.08)]" : ""}

          hover:shadow-[0_4px_16px_rgba(15,23,42,0.07)]
        `}
      >
        {/* Blue unread indicator */}
        {isUnread && (
          <div
            className="
              absolute
              left-0
              top-0
              bottom-0
              w-[3px]
              bg-blue-500
            "
          />
        )}

        {/* ==========================================
            MAIN ROW
        ========================================== */}

        <div
          className=" 
            px-4
            sm:px-5
            py-4
            flex
            items-center
            gap-3
            sm:gap-4
            cursor-pointer
          "
          onClick={() => handleNotificationClick(notification)}
        >
          {/* Icon */}
          <div
            className={`
              shrink-0
              w-10
              h-10
              sm:w-11
              sm:h-11
              rounded-xl
              flex
              items-center
              justify-center
              border

              ${
                isHighError
                  ? "bg-red-50 border-red-100"
                  : "bg-amber-50 border-amber-100"
              }
            `}
          >
            <IconFileSpreadsheet
              size={19}
              stroke={1.8}
              className={isHighError ? "text-red-500" : "text-amber-500"}
            />
          </div>

          {/* Content */}
          <div className="min-w-0 flex-1">
            {/* Title line */}
            <div
              className="
                flex
                flex-wrap
                items-center
                gap-2
              "
            >
              <h3
                className="
                  text-[13px]
                  sm:text-[14px]
                  font-semibold
                  text-slate-900
                  tracking-[-0.01em]
                "
              >
                {notification.title || "Notification"}
              </h3>

              {fileName && (
                <span
                  className="
                    inline-flex
                    items-center
                    max-w-[220px]
                    truncate
                    px-2
                    py-1
                    rounded-md
                    bg-slate-50
                    border
                    border-slate-200
                    text-[9px]
                    sm:text-[10px]
                    font-medium
                    text-slate-600
                  "
                >
                  {fileName}
                </span>
              )}
            </div>

            {/* Message */}
            <p
              className="
                mt-1
                text-[11px]
                sm:text-[12px]
                text-slate-500
                leading-5
              "
            >
              {notification.message || "Bulk processing task completed."}
            </p>

            {/* Processed */}
            {processed && (
              <div
                className="
                  mt-2
                  flex
                  items-center
                  flex-wrap
                  gap-x-3
                  gap-y-1
                  text-[10px]
                  sm:text-[11px]
                "
              >
                <span className="text-slate-400">Processed:</span>

                <span className="font-semibold text-slate-700">
                  {processed.processed}/{processed.total} rows
                </span>

                {processed.failed > 0 && (
                  <>
                    <span className="text-slate-300">•</span>

                    <span className="font-medium text-red-500">
                      {processed.failed} failed
                    </span>
                  </>
                )}

                <span className="text-slate-300">•</span>

                {hasErrorLogs && (
                  <button
                    type="button"
                    onClick={(event) => toggleExpanded(event, notification.id)}
                    className="
                      font-semibold
                      text-red-500
                      hover:text-red-600
                      hover:underline
                      transition-colors
                    "
                  >
                    {isExpanded ? "Hide Logs" : "View Error Logs"}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* ========================================
              ACTIONS
          ======================================== */}
          <div className="space-y-1">
            <div
              className="
              shrink-0
              flex
              items-center
              gap-1
              sm:gap-2
            "
              onClick={(event) => event.stopPropagation()}
            >
              {/* Error sheet */}
              {notification.bulkJobFileUrl && (
                <button
                  type="button"
                  onClick={(event) =>
                    handleDownload(event, notification.bulkJobFileUrl)
                  }
                  className="
                  hidden
                  sm:flex
                  items-center
                  gap-1.5
                  px-3
                  py-2
                  rounded-lg
                  border
                  border-red-100
                  bg-red-50/40
                  text-red-500
                  text-[10px]
                  font-semibold
                  transition-all
                  hover:bg-red-50
                  hover:border-red-200
                "
                >
                  <IconDownload size={14} stroke={1.8} />
                  Error Sheet
                </button>
              )}

              <button
                className={`
                  inline-flex
                  items-center
                  px-2
                  py-1
                  rounded-md
                  text-[9px]
                  font-semibold
                  border

                  ${
                    isHighError
                      ? "bg-red-50 text-red-600 border-red-100"
                      : status === "Needs Review"
                        ? "bg-amber-50 text-amber-600 border-amber-100"
                        : "bg-emerald-50 text-emerald-600 border-emerald-100"
                  }
                `}
              >
                {status}
              </button>
            </div>
            <div className="flex justify-end">
              <p className="text-slate-400 text-xs">
                {formatTime(notification.created_at)}
              </p>
            </div>
          </div>
        </div>

        {/* Mobile Error Sheet */}
        {notification.bulkJobFileUrl && (
          <div
            className="
              sm:hidden
              px-4
              pb-3
              flex
              justify-end
            "
          >
            <button
              type="button"
              onClick={(event) =>
                handleDownload(event, notification.bulkJobFileUrl)
              }
              className="
                flex
                items-center
                gap-1.5
                px-3
                py-2
                rounded-lg
                border
                border-red-100
                bg-red-50/40
                text-red-500
                text-[10px]
                font-semibold
              "
            >
              <IconDownload size={14} />
              Error Sheet
            </button>
          </div>
        )}

        {/* ==========================================
            ERROR LOG PANEL
        ========================================== */}

        {isExpanded && hasErrorLogs && renderErrorLogs(notification)}
      </div>
    );
  };

  /* =====================================================
     RENDER
  ===================================================== */

  return (
    <div
      className=" bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl transition-all duration-300
         
        text-slate-800
        mx-6 mb-2 rounded-3xl
      "
    >
      {/* =================================================
          HEADER
      ================================================= */}

      <div
        className="
          bg-white/20
          border-b
          border-[#D2D2D2]
        "
      >
        <div
          className="
            px-5
            sm:px-7
            py-4
            flex
            flex-col
            xl:flex-row
            xl:items-center
            justify-between
            gap-4
          "
        >
          {/* Left */}
          <div>
            <h1
              className="
                text-[20px]
                sm:text-[22px]
                font-semibold
                tracking-[-0.025em]
                text-slate-950
              "
            >
              Notification Center
            </h1>

            <p
              className="
                mt-0.5
                text-[11px]
                sm:text-[12px]
                text-slate-500
              "
            >
              Track and review background processing tasks and validation
              reports.
            </p>
          </div>

          {/* Header stats */}
          <div
            className="
              flex
              items-center
              gap-3
              flex-wrap
            "
          >
            {/* Success rate */}
            <div
              className="
                min-w-[145px]
                px-4
                py-2.5
                rounded-lg
                border
                border-slate-200
                bg-slate-50/60
                flex
                items-center
                gap-3
              "
            >
              <div
                className="
                  w-8
                  h-8
                  rounded-lg
                  bg-blue-50
                  border
                  border-blue-100
                  flex
                  items-center
                  justify-center
                "
              >
                <IconChartBar size={16} className="text-blue-500" />
              </div>

              <div>
                <p
                  className="
                    text-[9px]
                    uppercase
                    tracking-wider
                    font-semibold
                    text-slate-400
                  "
                >
                  Success Rate
                </p>

                <p
                  className="
                    text-[13px]
                    font-bold
                    text-slate-800
                  "
                >
                  {successRate}
                </p>
              </div>
            </div>

            {/* Failed rows */}
            <div
              className="
                min-w-[145px]
                px-4
                py-2.5
                rounded-lg
                border
                border-slate-200
                bg-slate-50/60
                flex
                items-center
                gap-3
              "
            >
              <div
                className="
                  w-8
                  h-8
                  rounded-lg
                  bg-amber-50
                  border
                  border-amber-100
                  flex
                  items-center
                  justify-center
                "
              >
                <IconAlertTriangle size={16} className="text-amber-500" />
              </div>

              <div>
                <p
                  className="
                    text-[9px]
                    uppercase
                    tracking-wider
                    font-semibold
                    text-slate-400
                  "
                >
                  Total Failed Rows
                </p>

                <p
                  className="
                    text-[13px]
                    font-bold
                    text-slate-800
                  "
                >
                  {totalFailedRows} rows
                </p>
              </div>
            </div>

            {/* Mark all */}
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              disabled={unreadNotifications === 0}
              className="
                h-10
                px-4
                rounded-lg
                border
                border-slate-300
                bg-white
                text-[11px]
                font-semibold
                text-slate-700
                flex
                items-center
                gap-2
                transition-all
                hover:bg-slate-50
                hover:border-slate-400
                disabled:opacity-40
                disabled:cursor-not-allowed
              "
            >
              <IconCheck size={15} stroke={1.8} />
              Mark all as read
            </button>
          </div>
        </div>
      </div>

      {/* =================================================
          FILTER BAR
      ================================================= */}

      <div
        className="
          bg-white/20
          border-b
          border-slate-200
        "
      >
        <div
          className="
            px-5
            sm:px-7
            py-4
            flex
            flex-col
            md:flex-row
            md:items-center
            justify-between
            gap-3
          "
        >
          {/* Tabs */}
          <div
            className="
              inline-flex
              self-start
              items-center
              rounded-lg
              border
              border-slate-200
              bg-slate-100/70
              p-1
            "
          >
            <button
              type="button"
              onClick={() => setActiveRole("All")}
              className={`
                px-3
                sm:px-4
                h-8
                rounded-md
                text-[10px]
                sm:text-[11px]
                font-semibold
                transition-all

                ${
                  activeRole === "All"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }
              `}
            >
              All Notifications
              <span
                className="
                  ml-1.5
                  px-1.5
                  py-0.5
                  rounded
                  bg-slate-100
                  text-[9px]
                  text-slate-500
                "
              >
                {totalNotifications}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveRole("Unread")}
              className={`
                px-3
                sm:px-4
                h-8
                rounded-md
                text-[10px]
                sm:text-[11px]
                font-semibold
                transition-all

                ${
                  activeRole === "Unread"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }
              `}
            >
              Unread
              {unreadNotifications > 0 && (
                <span
                  className="
                    ml-1.5
                    px-1.5
                    py-0.5
                    rounded
                    bg-red-50
                    text-red-500
                    text-[9px]
                  "
                >
                  {unreadNotifications}
                </span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setActiveRole("Read")}
              className={`
                px-3
                sm:px-4
                h-8
                rounded-md
                text-[10px]
                sm:text-[11px]
                font-semibold
                transition-all

                ${
                  activeRole === "Read"
                    ? "bg-white text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-800"
                }
              `}
            >
              Read
            </button>
          </div>

          {/* Search */}
          <div
            className="
              relative
              w-full
              md:w-[315px]
            "
          >
            <IconSearch
              size={16}
              className="
                absolute
                left-3
                top-1/2
                -translate-y-1/2
                text-slate-400
              "
            />

            <input
              value={searchFor}
              onChange={(event) => setSearchFor(event.target.value)}
              placeholder="Search file name, job title..."
              className="
                w-full
                h-9
                pl-9
                pr-3
                rounded-lg
                border
                border-slate-200
                bg-slate-50/50
                outline-none
                text-[11px]
                text-slate-700
                placeholder:text-slate-400
                transition-all
                focus:bg-white
                focus:border-blue-300
                focus:ring-2
                focus:ring-blue-50
              "
            />
          </div>
        </div>
      </div>

      {/* =================================================
          LIST
      ================================================= */}

      <main
        className="
          px-5
          sm:px-7
          py-6
        "
      >
        {filteredNotifications.length > 0 ? (
          <div className="space-y-7">
            {orderedGroups.map(([date, items]) => (
              <section key={date}>
                {/* Date */}
                <div
                  className="
                      flex
                      items-center
                      gap-3
                      mb-4
                    "
                >
                  <span
                    className="
                        shrink-0
                        text-[10px]
                        font-bold
                        tracking-[0.12em]
                        text-slate-400
                      "
                  >
                    {formatDateHeading(date)}
                  </span>

                  <div className="h-px flex-1 bg-slate-200" />
                </div>

                {/* Notifications */}
                <div className="space-y-3">{items.map(renderNotification)}</div>
              </section>
            ))}
          </div>
        ) : (
          <div
            className="
              min-h-[400px]
              rounded-xl
              border
              border-slate-200
              bg-white
              flex
              items-center
              justify-center
            "
          >
            <NoDataFound
              title="No Notifications Found"
              message={
                searchFor
                  ? "No notifications match your search."
                  : activeRole === "Unread"
                    ? "There are no unread notifications."
                    : activeRole === "Read"
                      ? "There are no read notifications."
                      : "New notifications will appear here."
              }
              mainClassName="rounded-3xl"
              subClassName="bg-transparent"
            />
          </div>
        )}
      </main>

      {/* =================================================
          BULK RESPONSE
      ================================================= */}

      {openResponse && (
        <BulkResponseHandler
          isOpen={openResponse}
          onClose={() => setOpenResponse(false)}
          bulkJobId={bulkJobId}
          notification={notification}
        />
      )}
    </div>
  );
};

export default NotificationView;

